import { Box, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { SurfaceCard } from './SurfaceCard';
import type { FinanceStatus } from '../../styles/tokens/status';

type MetricTone = 'neutral' | 'primary' | 'positive' | 'negative' | 'warning';

export interface MetricCardProps {
  label: string;
  value?: ReactNode;
  supportingValue?: ReactNode;
  icon?: ReactNode;
  tone?: MetricTone;
  loading?: boolean;
  stale?: boolean;
  to?: string;
  /** Optional sparkline data array for trend visualization */
  sparklineData?: number[];
}

const TONE_STATUS: Record<MetricTone, FinanceStatus> = {
  neutral: 'draft',
  primary: 'submitted',
  positive: 'approved',
  negative: 'rejected',
  warning: 'pending',
};

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const width = 80;
  const height = 24;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  });

  return (
    <Box sx={{ mt: 1 }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        <polyline
          points={points.join(' ')}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.6}
        />
      </svg>
    </Box>
  );
}

export function MetricCard({
  label,
  value,
  supportingValue,
  icon,
  tone = 'neutral',
  loading = false,
  stale = false,
  to,
  sparklineData,
}: MetricCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const statusPresentation = theme.palette.finance.status[TONE_STATUS[tone]];
  const toneColors =
    statusPresentation.color === 'default'
      ? { bg: 'action.hover' as const, color: 'text.secondary' as const }
      : {
          bg: `${statusPresentation.color}.light` as const,
          color: `${statusPresentation.color}.dark` as const,
        };

  return (
    <SurfaceCard
      surfaceRole={to ? 'interactive' : 'standard'}
      padding="standard"
      component={to ? NavLink : undefined}
      to={to}
      aria-label={to ? label : undefined}
      sx={{ height: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {label}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width="60%" height={40} />
          ) : (
            <Typography
              variant="h3"
              component="p"
              color={stale ? 'text.disabled' : 'text.primary'}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {value ?? '\u2014'}
            </Typography>
          )}
          {sparklineData && !loading && (
            <MiniSparkline
              data={sparklineData}
              color={theme.palette.primary.main}
            />
          )}
          {supportingValue && !loading && !stale && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
              {supportingValue}
            </Typography>
          )}
          {stale && !loading && (
            <Typography variant="caption" color="warning.main" component="p" sx={{ mt: 0.75 }}>
              {t('common:states.stale.message')}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: theme.financeLayout.cardLayout.borderRadius,
              flexShrink: 0,
              bgcolor: toneColors.bg,
              color: toneColors.color,
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </SurfaceCard>
  );
}
